import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { COUNTRY, IS_HOSTED_INSURANCE } from "config";
import { animateRouteProps } from "constants/common";
import React from "react";
import { useSelector } from "react-redux";

import {
  customStylesSelector,
  paymentSelector,
} from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";

interface SuccessMessageI {
  message: string;
  showLineOne?: boolean;
}

const SuccessMessage: React.FC<SuccessMessageI> = ({
  message,
  showLineOne = true,
}) => {
  const { t } = useT();
  const customStyles = useSelector(customStylesSelector);
  const { supportEmail } = useSelector((state: RootState) => state.client);
  const productIdentifier = useSelector(
    (state: RootState) =>
      state.client.availableProducts[0]?.product?.product_identifier
  );
  const { bindResponse } = useSelector(paymentSelector);
  const blockedAlerts = bindResponse?.response?.blockedAlerts;
  const inspectionData = bindResponse?.response?.inspectionResponse;
  const isMsig = COUNTRY === "sg" && productIdentifier === "PET";
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        height: "100%",
      }}
      {...animateRouteProps}
    >
      <Box
        data-cy="success-message"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          m: "auto",
          width: "100%",
          maxWidth: "800px",
        }}
      >
        {blockedAlerts && (
          <Box
            sx={{
              mt: 2,
              p: 3,
              mb: 4,
              bgcolor: "#fff3cd",
              color: "#856404",
              border: "1px solid #ffeeba",
              borderRadius: 2,
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <WarningIcon color="warning" />
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t("ALERT_IMPORTANT_INFORMATION")}
              </Typography>
              <Typography>{blockedAlerts}</Typography>
            </Box>
          </Box>
        )}

        {inspectionData && (
          <Box
            sx={{
              mt: 2,
              p: 3,
              mb: 4,
              bgcolor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: 2,
              width: "100%",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}
            >
              {t("INSPECTION_INFORMATION") || "Inspection Information"}
            </Typography>
            <TableContainer
              component={Paper}
              sx={{ boxShadow: "none", border: "1px solid #dee2e6" }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}
                    >
                      Field
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, backgroundColor: "#f8f9fa" }}
                    >
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(inspectionData).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          borderRight: "1px solid #dee2e6",
                        }}
                      >
                        {key.charAt(0).toUpperCase() +
                          key.slice(1).replace(/([A-Z])/g, " $1")}
                      </TableCell>
                      <TableCell>{String(value || "-")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Box
          sx={{
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100px",
            width: "100px",
            background: customStyles.stepper.backgroundColor,
          }}
        >
          <CheckIcon
            data-cy="success-check-icon"
            style={{
              color: customStyles.stepper.step.color,
              fontSize: "75px",
            }}
          />
        </Box>
        <Typography
          sx={{
            color: customStyles.muiTheme.palette.primary.main,
            fontSize: "24px",
            fontWeight: 700,
            mt: "30px",
          }}
        >
          {message}
        </Typography>
        <Typography
          sx={{
            color: customStyles.muiTheme.palette.text.tertiary,
            fontSize: "20px",
            fontWeight: 500,
            textAlign: "center",
            mt: "24px",
          }}
        >
          {showLineOne ? t("SUCCESS_PAGE_CONTENT.line1") : ""}
        </Typography>
      </Box>
      {IS_HOSTED_INSURANCE && (
        <Box
          sx={{
            p: "15px",
            borderRadius: "12px",
            mb: "20px",
            border: "2px solid",
            borderColor: customStyles.muiTheme.palette.primary.main,
            backgroundColor: `${customStyles.muiTheme.palette.primary.main}20`,
            maxWidth: "80%",
          }}
        >
          <Typography
            sx={{
              color: customStyles.muiTheme.palette.text.tertiary,
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {isMsig
              ? t("SUCCESS_PAGE_CONTENT.msigLine2")
              : t("SUCCESS_PAGE_CONTENT.line2")}
            <span style={{ color: customStyles.muiTheme.palette.text.primary }}>
              {isMsig
                ? "service@sg.msig-asia.com "
                : supportEmail
                ? supportEmail
                : t("SUCCESS_PAGE_CONTENT.email")}
            </span>
            {t("SUCCESS_PAGE_CONTENT.line3")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SuccessMessage;
